import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import InformationDisplay, { displayInformation, informationDisplayLabel } from "@/components/InformationDisplay";
import NewsRelevanceDisplay from "@/components/NewsRelevanceDisplay";
import WhoResearchContext from "@/components/WhoResearchContext";
import PredictionContext from "@/components/PredictionContext";
import { useFinanceMonitor, useMonitorBootstrap } from "@/hooks/useNews";
import { Helmet } from "react-helmet-async";
import { recommendVideoChannel } from "@shared/videoChannels";
import {
  Activity,
  ArrowRight,
  BarChart3,
  CircleDollarSign,
  CloudLightning,
  Database,
  Globe2,
  HeartPulse,
  MapPin,
  ShieldAlert,
  Tv2,
} from "lucide-react";
import React, { Component, useEffect, useState } from "react";

class MonitorErrorBoundary extends Component<{ children: React.ReactNode }, { error: string | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(err: unknown) {
    return { error: String(err) };
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '32px', color: '#ff6b6b', fontFamily: 'monospace' }}>
          <strong>Monitor render error:</strong>
          <pre style={{ marginTop: '12px', whiteSpace: 'pre-wrap', fontSize: '12px' }}>{this.state.error}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

function displayLocation(location?: string) {
  if (!location) return "Location pending";
  return location.length > 54 ? `${location.slice(0, 51)}...` : location;
}

function displaySignalLocation(signal?: { location?: string; publisherCountry?: string }) {
  if (signal?.location) return displayLocation(signal.location);
  if (signal?.publisherCountry) return `Publisher: ${displayLocation(signal.publisherCountry)}`;
  return "Location pending";
}

const Monitor: React.FC = () => {
  const { data: monitor, isLoading } = useMonitorBootstrap();
  const { data: financeMonitor, isLoading: isFinanceLoading } = useFinanceMonitor();
  const [dossierEnabled, setDossierEnabled] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/research-dossiers/config", { signal: controller.signal, cache: "no-store" })
      .then((response) => response.ok ? response.json() : null)
      .then((config) => {
        if (!controller.signal.aborted) setDossierEnabled(config?.enabled === true);
      })
      .catch(() => { if (!controller.signal.aborted) setDossierEnabled(false); });
    return () => controller.abort();
  }, []);
  const marketNews = monitor?.marketNews ?? [];
  const allSourceSignals = monitor?.sources.flatMap((source) =>
    source.signals.map((signal) => ({ ...signal, sourceStatus: source.status })),
  ) ?? [];
  const sourceSignals = monitor?.sources.flatMap((source) =>
    source.signals.slice(0, 4).map((signal) => ({
      ...signal,
      sourceStatus: source.status,
    })),
  ) ?? [];
  const eventGroups = monitor?.eventGroups;
  const groupedDisplayReady = eventGroups?.displayEnabled === true
    && eventGroups.eventTracking.status === "tracked"
    && eventGroups.groups !== null;
  const eventTrackingAvailable = eventGroups?.eventTracking.status === "tracked";
  const groupedRows = groupedDisplayReady
    ? (monitor?.sources.flatMap((source) => {
      const sourceSignalsById = new Map(source.signals.map((signal) => [`monitor:${signal.id}`, signal]));
      const sourceGroups = (eventGroups.groups ?? [])
        .map((group) => {
          const signal = sourceSignalsById.get(group.representativeSignalId)
            ?? group.reports.map((report) => sourceSignalsById.get(report.sourceSignalId)).find(Boolean);
          return signal ? { ...signal, sourceStatus: source.status, eventGroup: group } : null;
        })
        .filter((row): row is NonNullable<typeof row> => row !== null)
        .slice(0, 4);
      return sourceGroups;
    }) ?? [])
    : [];
  const ungroupedRows = groupedDisplayReady
    ? (monitor?.sources.flatMap((source) => {
      const represented = new Set((eventGroups.groups ?? []).flatMap((group) => group.reports.map((report) => report.sourceSignalId)));
      return source.signals
        .filter((signal) => signal.eventTracking?.status === "unsupported" && !represented.has(`monitor:${signal.id}`))
        .slice(0, 4)
        .map((signal) => ({ ...signal, sourceStatus: source.status, eventGroup: null, isUnsupportedArticle: true as const }));
    }) ?? [])
    : [];
  const osintRows = groupedDisplayReady
    ? [...groupedRows, ...ungroupedRows]
    : sourceSignals.map((signal) => ({ ...signal, eventGroup: null, isUnsupportedArticle: false as const }));
  const retainedReportCount = groupedDisplayReady
    ? (eventGroups.groups ?? []).reduce((sum, group) => sum + group.retainedReportCount, 0)
    : null;
  const financeCards = financeMonitor?.cards ?? [];
  const predictions = financeMonitor?.predictions ?? [];
  const highCriticalRecordCount = eventGroups?.counts.highCriticalRecordCount
    ?? allSourceSignals.filter((signal) => signal.severity === "high" || signal.severity === "critical").length;
  const latestWeather = sourceSignals.find((signal) => signal.source === "NOAA");
  const latestEarthquake = sourceSignals.find((signal) => signal.source === "USGS");
  const latestHealth = sourceSignals.find((signal) => signal.source === "WHO");
  const latestGeo = sourceSignals.find((signal) => signal.source === "GDELT");
  const latestGlobalHazard = sourceSignals.find((signal) => signal.source === "GDACS");
  const sourceCards = [
    {
      label: "Severe Weather",
      source: "NOAA",
      icon: CloudLightning,
      signal: latestWeather,
      empty: "No qualifying severe weather signal.",
    },
    {
      label: "Earthquakes",
      source: "USGS",
      icon: Activity,
      signal: latestEarthquake,
      empty: "No M5+ earthquake signal.",
    },
    {
      label: "Health Alerts",
      source: "WHO",
      icon: HeartPulse,
      signal: latestHealth,
      empty: "No qualifying health alert.",
    },
    {
      label: "Geo Headlines",
      source: "GDELT",
      icon: Globe2,
      signal: latestGeo,
      empty: "No potentially market-relevant geo article.",
    },
    {
      label: "Global Hazards",
      source: "GDACS",
      icon: ShieldAlert,
      signal: latestGlobalHazard,
      empty: "No qualifying GDACS hazard signal.",
    },
  ];
  const recommendedChannel = recommendVideoChannel(sourceSignals[0]);
  const topMarketHeadline = marketNews[0];
  const pageTitle = topMarketHeadline
    ? `Monitor — ${topMarketHeadline.title} | Tylee Smart Markets`
    : "Monitor — Markets, Policy, Energy & Conflict Signals | Tylee Smart Markets";
  const pageDescription = topMarketHeadline
    ? `${topMarketHeadline.summary || topMarketHeadline.title} Public APIs and potentially market-relevant news in one monitor. Text-based relevance does not confirm a market reaction.`
    : predictions.length
      ? "Situational monitor with provider states, dated oil and rates observations, geopolitical reports, and Polymarket prices."
      : "Situational monitor with provider states, dated oil and rates observations, and geopolitical reports.";

  return (
    <MonitorErrorBoundary>
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href="https://tyl.ee/monitor" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content="https://tyl.ee/monitor" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>
      <div className="tsm-home-shell theme-amber">
        <AppHeader brandText="Monitor" />

      <main className="monitor-main">
        <section className="monitor-lede" aria-labelledby="monitor-title">
          <div>
            <p className="home-kicker">FINANCE-FIRST SITUATIONAL MONITOR</p>
            <h1 id="monitor-title">Markets, policy, energy, and conflict signals.</h1>
          </div>
          <div className="monitor-ledger" aria-label="Monitor scope">
            <span>Oil</span>
            <span>Rates</span>
            <span>War</span>
            <span>Politics</span>
            {predictions.length > 0 && <span>Polymarket</span>}
          </div>
          {dossierEnabled && <a className="analytics-action-btn" href="/analytics?dossier=1&amp;lane=tradfi" aria-label="Investigate a market quote in Analytics">
            Investigate a quote <ArrowRight size={14} aria-hidden="true" />
          </a>}
          <a className="analytics-action-btn" href="/analytics#event-evidence-review" aria-label="Review retained evidence for one event in Analytics">
            Review retained event evidence <ArrowRight size={14} aria-hidden="true" />
          </a>
        </section>

        <section className="monitor-grid" aria-label="Monitor dashboard">
          <div className="monitor-column">
            <section className="monitor-panel monitor-coverage" aria-labelledby="monitor-coverage-title">
              <div>
                <p className="home-kicker">RECOMMENDED COVERAGE</p>
                <h2 id="monitor-coverage-title">Watch live in the Video newsroom.</h2>
                <p>
                  {recommendedChannel
                    ? `${recommendedChannel.name} is a general category-based broadcaster suggestion. No match to this event or a program is established.`
                    : "Open the current broadcaster directory and choose one channel at a time. Recommendations do not establish event or program coverage."}
                </p>
              </div>
              <a href={recommendedChannel ? `/video/${recommendedChannel.slug}` : "/video"}>
                <Tv2 size={16} aria-hidden="true" />
                {recommendedChannel ? `Watch ${recommendedChannel.name}` : "Open Video"}
                <ArrowRight size={15} aria-hidden="true" />
              </a>
            </section>

            <section className="monitor-panel monitor-finance" aria-labelledby="monitor-finance-title">
              <div className="monitor-panel-head">
                <div>
                  <p className="home-kicker">FINANCIAL INFO</p>
                  <h2 id="monitor-finance-title">Market pulse</h2>
                </div>
                <CircleDollarSign size={16} aria-hidden="true" />
              </div>

              <section className="monitor-probability-deck" aria-labelledby="monitor-probability-title">
                <div className="monitor-subsection-head">
                  <div>
                    <p className="home-kicker">POLYMARKET</p>
                    <h3 id="monitor-probability-title">Probability deck</h3>
                  </div>
                  <BarChart3 size={15} aria-hidden="true" />
                </div>
                <div className="monitor-poly-list">
                  {predictions.map((prediction) => {
                    const content = <>
                      <span>{prediction.question}</span>
                      <small>Polymarket event · selected market details below</small>
                      <InformationDisplay information={prediction.information} expandable={false} />
                    </>;
                    return <div key={prediction.id} className="monitor-prediction-row">
                      {prediction.sourceUrl ? <a href={prediction.sourceUrl} target="_blank" rel="noreferrer">{content}</a> : <div>{content}</div>}
                      <PredictionContext prediction={prediction} />
                    </div>;
                  })}
                  {!isFinanceLoading && predictions.length === 0 && (
                    <div className="monitor-empty">{financeMonitor?.predictionSource ? informationDisplayLabel(financeMonitor.predictionSource) : "Polymarket data unavailable."}</div>
                  )}
                </div>
                {financeMonitor?.predictionSource && <InformationDisplay information={financeMonitor.predictionSource} />}
              </section>

              <div className="monitor-finance-grid">
                {isFinanceLoading && <div className="monitor-empty">Loading finance cards...</div>}
                {financeCards.map((card) => {
                  const information = displayInformation(card.information);
                  const valueAvailable = !information || (information.data.validity === "valid" && ["available", "partial"].includes(information.data.availability));
                  return (
                  <article key={card.id} className={`monitor-finance-card is-${card.status}`}>
                    <div>
                      <span>{card.kind}</span>
                      <strong>{card.title}</strong>
                    </div>
                    <b>{valueAvailable ? card.primary : "Value unavailable"}</b>
                    {valueAvailable && <p>{card.secondary}</p>}
                    {valueAvailable && <ul className="monitor-finance-drivers">
                      {card.drivers.slice(0, 2).map((driver) => (
                        <li key={driver}>{driver}</li>
                      ))}
                    </ul>}
                    <NewsRelevanceDisplay newsEvidence={card.newsEvidence} />
                    <small>{card.provider}</small>
                    <InformationDisplay information={card.information} />
                  </article>
                  );
                })}

                <article className="monitor-finance-card is-risk">
                  <div>
                    <span>macro intel</span>
                    <strong>High-severity signals</strong>
                  </div>
                  <b>{highCriticalRecordCount}</b>
                  <p>High-severity public-source signals across weather, earthquakes, health, and geopolitics. Physical severity is not a financial-loss estimate.</p>
                  <small>{monitor?.cache.redisConfigured ? "Redis cached" : "Memory only"}</small>
                </article>
              </div>
            </section>
          </div>

          <aside className="monitor-side" aria-label="Public data sources">
            <section className="monitor-panel monitor-api-panel" aria-labelledby="monitor-api-title">
              <div className="monitor-panel-head">
                <div>
                  <p className="home-kicker">PUBLIC API STATES</p>
                  <h2 id="monitor-api-title">Source information</h2>
                </div>
                <Database size={16} aria-hidden="true" />
              </div>
              <div className="monitor-api-grid">
                {sourceCards.map(({ label, source, icon: Icon, signal, empty }) => {
                  const snapshot = monitor?.sources.find((item) => item.id.toLowerCase() === source.toLowerCase());
                  const state = displayInformation(snapshot?.information);
                  const emptyMessage = state?.data.availability === "empty" && state.provider.health === "healthy" ? empty : "No signal value available.";
                  return (
                  <article key={label} className={`monitor-api-card is-${signal?.severity ?? "quiet"}`}>
                    <div>
                      <Icon size={15} aria-hidden="true" />
                      <span>{source}</span>
                    </div>
                    <strong>{label}</strong>
                    <p>{signal?.title ?? emptyMessage}</p>
                    <InformationDisplay information={snapshot?.information} />
                    <small>
                      <MapPin size={11} aria-hidden="true" />
                      {displaySignalLocation(signal)}
                    </small>
                  </article>
                  );
                })}
              </div>
            </section>
          </aside>
        </section>

        <section className="home-news-band monitor-osint-band" aria-labelledby="monitor-osint-title">
          <div className="home-news-heading">
            <div>
              <p className="home-kicker">
                OSINT
                <span className="home-news-count">
                  {groupedDisplayReady
                    ? `${groupedRows.length} ${groupedRows.length === 1 ? "GROUP" : "GROUPS"} · ${ungroupedRows.length} UNGROUPED SIGNALS`
                    : `${osintRows.length} SIGNALS`}
                </span>
              </p>
              <h2 id="monitor-osint-title">Source event signals</h2>
            </div>
          </div>
          <p className="monitor-event-denominator" aria-label="Event group counts">
            Source records: {eventGroups?.counts.sourceRecordCount ?? allSourceSignals.length}
            {" · Tracked source records: "}{eventTrackingAvailable ? eventGroups.counts.trackedRecordCount ?? "unavailable" : "unavailable"}
            {" · Known event groups: "}{eventGroups?.counts.uniqueEventCount ?? "unavailable"}
            {" · Groups shown: "}{groupedDisplayReady ? groupedRows.length : "unavailable"}
            {" · Unsupported source records: "}{eventTrackingAvailable ? eventGroups.counts.untrackedRecordCount ?? "unavailable" : "unavailable"}
            {" · Unsupported records shown: "}{groupedDisplayReady ? ungroupedRows.length : "unavailable"}
            {" · Retained reports: "}{retainedReportCount ?? "unavailable"}
            {" · High-critical source records: "}{highCriticalRecordCount}/{eventGroups?.counts.sourceRecordCount ?? allSourceSignals.length}
            {" · High-critical groups: "}{eventGroups?.counts.highCriticalGroupCount ?? "unavailable"}
          </p>
          {(!eventGroups || eventGroups.eventTracking.status !== "tracked" || !eventGroups.displayEnabled) && (
            <p className="monitor-event-tracking-note" role="status">
              {!eventGroups
                ? "Event tracking metadata unavailable; showing the legacy source projection."
                : eventGroups.eventTracking.status === "tracked"
                ? "Grouped display is disabled; showing the legacy source projection."
                : `Event tracking ${eventGroups.eventTracking.status}; showing the legacy source projection.${eventGroups.eventTracking.reason ? ` ${eventGroups.eventTracking.reason}` : ""}`}
            </p>
          )}
          <div className="monitor-osint-list" aria-live="polite">
            {osintRows.map((signal) => (
              signal.eventGroup ? (
                <article key={`${signal.source}-${signal.eventGroup.id}`} className="monitor-event-group">
                  <a
                    href={signal.url || "/monitor"}
                    target={signal.url ? "_blank" : undefined}
                    rel={signal.url ? "noreferrer" : undefined}
                  >
                    <strong>{signal.title}</strong>
                    {signal.summary && <p>{signal.summary}</p>}
                    {signal.marketImpact && <p className="osint-impact">{signal.marketImpact}</p>}
                    <NewsRelevanceDisplay relevance={signal.relevance} newsEvidence={signal.newsEvidence} />
                    <small>{signal.source} · <span className={`severity-badge is-${signal.severity}`}>{signal.severity}</span>{signal.location ? ` · ${signal.location}` : signal.publisherCountry ? ` · publisher ${signal.publisherCountry}` : ''}</small>
                    <InformationDisplay information={signal.information} expandable={false} />
                  </a>
                  <p className="monitor-group-chronology">
                    {signal.eventGroup.currentReportCount} current reports · {signal.eventGroup.retainedReportCount} retained reports · chronology starts {signal.eventGroup.chronologyStartedAt}
                    {signal.eventGroup.historyPrunedBefore ? ` · Earlier history pruned before ${signal.eventGroup.historyPrunedBefore}` : " · Earlier history may predate this retained window"}
                  </p>
                  <div className="monitor-report-links" aria-label={`Retained reports for ${signal.title}`}>
                    {signal.eventGroup.reports.map((report) => report.url ? (
                      <a key={report.reportKey} className="monitor-report-link" href={report.url} target="_blank" rel="noreferrer">
                        Report {report.providerReportId}
                      </a>
                    ) : (
                      <span key={report.reportKey} className="monitor-report-link">Report {report.providerReportId}</span>
                    ))}
                  </div>
                </article>
              ) : (
                <a
                  key={`${signal.source}-${signal.id}`}
                  href={signal.url || "/monitor"}
                  target={signal.url ? "_blank" : undefined}
                  rel={signal.url ? "noreferrer" : undefined}
                >
                  <strong>{signal.title}</strong>
                  {signal.summary && <p>{signal.summary}</p>}
                  {signal.marketImpact && <p className="osint-impact">{signal.marketImpact}</p>}
                  <NewsRelevanceDisplay relevance={signal.relevance} newsEvidence={signal.newsEvidence} />
                  <small>
                    {signal.source} · <span className={`severity-badge is-${signal.severity}`}>{signal.severity}</span>
                    {signal.location ? ` · ${signal.location}` : signal.publisherCountry ? ` · publisher ${signal.publisherCountry}` : ''}
                    {signal.isUnsupportedArticle ? " · Article signal · unsupported for event grouping" : ""}
                  </small>
                  <InformationDisplay information={signal.information} expandable={false} />
                </a>
              )
            ))}
            {!isLoading && osintRows.length === 0 && (
              <div className="monitor-empty">No source event records returned. Check the source information above.</div>
            )}
          </div>
        </section>

        <section className="home-news-band monitor-news-band" aria-labelledby="monitor-news-title">
          <div className="home-news-heading">
            <div>
              <p className="home-kicker">
                POTENTIALLY MARKET-RELEVANT NEWS
                <span className="home-news-count">{marketNews.length || "--"} HEADLINES</span>
              </p>
              <h2 id="monitor-news-title">Filtered tape</h2>
            </div>
            <a href="/news" className="home-news-link">Open terminal</a>
          </div>

          <div className="home-news-list" aria-live="polite">
            {isLoading && <div className="home-news-empty">Loading tape...</div>}
            {!isLoading && marketNews.length === 0 && (
              <div className="home-news-empty">No potentially market-relevant headlines matched.</div>
            )}
            {marketNews.map((item) => (
              <a
                key={item.id}
                href={item.url || "/news"}
                target={item.url ? "_blank" : undefined}
                rel={item.url ? "noreferrer" : undefined}
                className="home-news-row"
              >
                <span className="home-news-provider">{item.source}</span>
                <span className="home-news-title">
                  {item.title}
                  <NewsRelevanceDisplay relevance={item.relevance} newsEvidence={item.newsEvidence} compact />
                </span>
                <InformationDisplay information={item.information} expandable={false} />
              </a>
            ))}
          </div>
        </section>
        <WhoResearchContext context={monitor?.intelligenceContext} />
      </main>
      <Footer />
      </div>
    </>
    </MonitorErrorBoundary>
  );
};

export default Monitor;
